import {
  CloseRounded as CloseRoundedIcon,
  SearchRounded as SearchRoundedIcon,
  WifiTetheringRounded as WifiTetheringRoundedIcon
} from '@mui/icons-material'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import {
  Autocomplete,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  InputAdornment,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Tab,
  TextField
} from '@mui/material'
import { t } from 'i18next'
import { useEffect, useState } from 'react'

import { useDevices } from '../../Devices/Contexts/DevicesContext.jsx'
import InlineMessage from '../../Shared/Components/InlineMessage.jsx'

const SearchDevices = ({ onSelected, isOpen, onClose }) => {
  const {
    useFetchBrands,
    useSearchDevicesAutomations,
    useFetchModels,
    useFetchAutomationDevices
  } = useDevices()

  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState('by-model')
  const [model, setModel] = useState({ model: null })
  const [brands, setBrands] = useState([])
  const [models, setModels] = useState([])
  const [searchedDevices, setSearchedDevices] = useState([])
  const [isSearchDevicesLoading, setIsSearchDevicesLoading] = useState(false)
  const [devices, setDevices] = useState([])
  const [isDevicesLoading, setIsDevicesLoading] = useState(false)

  useEffect(() => {
    useFetchBrands().then(({ data }) => setBrands(data))
  }, [])

  const handleChange = (_event, value) => setTab(value)

  const handleClose = () => onClose()

  const onChangeBrand = (_, value) => {
    setModel({
      ...model,
      brand: value,
      model: null,
      provisioningSetup: null
    })

    if (!value) {
      return
    }

    useFetchModels(value.nameEncoded).then(({ data }) => setModels(data))
  }

  const onChangeModel = (_, value) => {
    setModel({
      ...model,
      model: value
    })

    if (!value) {
      return
    }
    setIsDevicesLoading(true)
    useFetchAutomationDevices({
      filters: { modelIds: [value.id] },
      page: 1,
      pageSize: 99999
    })
      .then((res) => setDevices(res.data))
      .finally(() => setIsDevicesLoading(false))
  }

  const handleSelectedDevice = (value) => {
    onSelected(value)
    handleClose()
  }

  const handleSearchByNameInputChange = (_, value) => {
    setIsSearchDevicesLoading(true)
    useSearchDevicesAutomations({ payload: { fullText: value } })
      .then(({ data }) => setSearchedDevices(data))
      .finally(() => setIsSearchDevicesLoading(false))
  }

  return (
    <Dialog
      open={isOpen}
      scroll="body"
      onClose={() => handleClose()}
      fullWidth={true}
      maxWidth={'md'}
    >
      <DialogActions sx={{
        justifyContent: 'right',
        p: '16px'
      }}>
        <CloseRoundedIcon
          onClick={() => {
            handleClose()
          }}
          color="rgba(105, 105, 105, .2)"
        />
      </DialogActions>

      <DialogTitle
        display="flex"
        justifyContent="center"
        variant="h4"
      >
        {t('actions.findSensor')}
      </DialogTitle>

      <DialogContent
        align="center"
        valign="center"
        sx={{
          padding: '10px 10px'
        }}
      >
        <TabContext value={tab}>
          <TabList
            sx={{
              marginTop: 4,
              borderBottom: 1,
              borderColor: 'divider'
            }}
            onChange={handleChange}
            aria-label="searchDevices"
          >
            <Tab
              aria-label={`tab ${t('actions.searchByBrandAndModel')}`}
              label={t('actions.searchByBrandAndModel')}
              value="by-model"
            />
            <Tab
              aria-label={`tab ${t('actions.searchByName')}`}
              data-testid="tab-cerca-per-nome"
              label={t('actions.searchByName')}
              value="by-name"
            />
          </TabList>
          <TabPanel
            value="by-model"
            sx={{ px: 0 }}
          >
            <Stack
              sx={{
                marginTop: '15px'
              }}
              direction="row"
              spacing={2}
              justifyContent="space-between"
            >
              <Autocomplete
                id="chooseModel-Brand"
                name="brand"
                fullWidth
                options={brands}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={model?.brand || { name: '' }}
                onChange={onChangeBrand}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('devices.chooseBrand')}
                    variant="standard"
                  />
                )}
              />

              <Autocomplete
                id="chooseModel-Model"
                name="model"
                fullWidth
                disabled={!models.length}
                options={models}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) =>
                  option && value && option.id === value.id
                }
                value={model?.model || { name: '' }}
                onChange={onChangeModel}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('devices.chooseModel')}
                    variant="standard"
                  />
                )}
              />
            </Stack>
            <Box sx={{ marginTop: '20px' }}>
              {!isDevicesLoading && !devices.length && (
                <InlineMessage
                  message={
                    'Nessun device trovato per marca e modello selezionati'
                  }
                />
              )}
              {!isDevicesLoading &&
                devices.map((device) => (
                  <ListItem
                    divider
                    aria-label={`${t('actions.selectDevice', 'Seleziona dispositivo')}: ${device.name}`} key={device.id}
                    onClick={() => handleSelectedDevice(device)}
                    role='button'
                    tabIndex={0}
                    sx={{ cursor: 'pointer' }}
                  >
                    <ListItemIcon>
                      <WifiTetheringRoundedIcon fontSize="large" />
                    </ListItemIcon>
                    <ListItemText
                      primaryTypographyProps={{
                        fontSize: '0.8rem',
                        fontWeight: 500
                      }}
                      primary={device.name}
                      secondary={device.address}
                    />
                  </ListItem>
                ))}
            </Box>
          </TabPanel>
          <TabPanel
            value="by-name"
            sx={{ px: 0 }}
          >
            <Autocomplete
              sx={{
                marginTop: '15px',
                flexGrow: 1,
                '& .MuiAutocomplete-inputRoot': {
                  paddingRight: '0 !important'
                }
              }}
              options={searchedDevices}
              open={open}
              onOpen={() => setOpen(true)}
              onClose={() => setOpen(false)}
              filterOptions={(x) => x}
              onInputChange={handleSearchByNameInputChange}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              loading={isSearchDevicesLoading}
              getOptionLabel={(option) => option.name}
              renderOption={(props, option) => (
                <ListItem
                  {...props}
                  divider
                  key={option.id}
                  onClick={() => handleSelectedDevice(option)}
                  sx={{ cursor: 'pointer' }}
                >
                  <ListItemIcon>
                    <WifiTetheringRoundedIcon fontSize="large" />
                  </ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={{
                      fontSize: '0.8rem',
                      fontWeight: 500
                    }}
                    primary={option.name}
                    secondary={option.address}
                  />
                </ListItem>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('common.search')}
                  variant="standard"
                  slotProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <InputAdornment position="end">
                        <Icon
                          color="inherit"
                          edge="end"
                        >
                          {isSearchDevicesLoading && (
                            <CircularProgress
                              edge="end"
                              size={20}
                            />
                          )}
                          {!isSearchDevicesLoading && (
                            <SearchRoundedIcon
                              edge="end"
                              size={20}
                            />
                          )}
                        </Icon>
                      </InputAdornment>
                    )
                  }}
                />
              )}
            />
          </TabPanel>
        </TabContext>
      </DialogContent>
    </Dialog>
  )
}

export default SearchDevices
